/**
 * Maison De Chez product page extras.
 *
 * 1. Thumbnail galleries - for the thumbs-left / thumbs-right layouts, clicking a
 *    thumbnail swaps the visible media. The grid layouts need no JS at all, so
 *    nothing here runs for them.
 * 2. Size guide - opens the size guide page inside a dialog instead of navigating
 *    away. The content is fetched from the real page on first open, so it always
 *    matches whatever is published there.
 */
(() => {
  /* ---------------------------------------------------------------- gallery */

  const activate = (gallery, index) => {
    const figures = gallery.querySelectorAll('.mdc-product__media');
    const thumbs = gallery.querySelectorAll('.mdc-product__thumb');
    if (!figures.length) return;

    const next = Math.max(0, Math.min(index, figures.length - 1));

    figures.forEach((figure, i) => {
      figure.toggleAttribute('data-active', i === next);
    });

    thumbs.forEach((thumb, i) => {
      const on = i === next;
      thumb.setAttribute('aria-selected', on ? 'true' : 'false');
      thumb.tabIndex = on ? 0 : -1;
    });
  };

  const initGallery = (gallery) => {
    const thumbs = [...gallery.querySelectorAll('.mdc-product__thumb')];
    if (!thumbs.length) return;

    activate(gallery, 0);

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => activate(gallery, i));
      thumb.addEventListener('keydown', (event) => {
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
          : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1
          : 0;
        if (!step) return;
        event.preventDefault();
        const target = (i + step + thumbs.length) % thumbs.length;
        activate(gallery, target);
        thumbs[target].focus();
      });
    });
  };

  /* ------------------------------------------------------------- size guide */

  const openSizeGuide = async (trigger) => {
    const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!dialog) return;

    const body = dialog.querySelector('[data-size-guide-body]');
    const url = trigger.dataset.sizeGuideUrl;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    if (body && !body.dataset.loaded && url) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(String(response.status));
        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        // Take the whole section wrapper, not just the .size-guide div: the
        // section's <style> and <script> are siblings of it, so grabbing the
        // div alone gives markup with no CSS and dead tab buttons.
        const guide = parsed.querySelector('.size-guide');
        const content =
          (guide && guide.closest('.shopify-section')) ||
          guide ||
          parsed.querySelector('main .shopify-section:last-of-type') ||
          parsed.querySelector('main');
        if (content) {
          body.innerHTML = '';
          body.appendChild(content);
          runScripts(body);
          filterToProduct(body, trigger.dataset.productCollections);
          body.dataset.loaded = 'true';
        } else {
          throw new Error('no content');
        }
      } catch {
        body.innerHTML = `<p class="mdc-size-guide__fallback">We could not load the size guide here. <a href="${url}">Open it on its own page</a>.</p>`;
        body.dataset.loaded = 'true';
      }
    }
  };

  /**
   * Scripts inserted via innerHTML never execute. Recreate each one so the
   * section's own tab and cm/inches logic actually binds inside the dialog.
   */
  const runScripts = (root) => {
    root.querySelectorAll('script').forEach((old) => {
      const fresh = document.createElement('script');
      [...old.attributes].forEach((attr) => fresh.setAttribute(attr.name, attr.value));
      fresh.textContent = old.textContent;
      old.replaceWith(fresh);
    });
  };

  /**
   * Narrow the charts to the one that fits this product.
   *
   * 1. If any chart is explicitly assigned to a collection this product is in,
   *    show only those.
   * 2. Otherwise fall back to charts with no collections set, which act as
   *    catch-alls.
   * 3. If there are none of those either, show everything rather than leaving
   *    the customer a blank panel.
   */
  const filterToProduct = (body, productCollections) => {
    const mine = (productCollections || '').split(/\s+/).filter(Boolean);

    const tabs = [...body.querySelectorAll('.sg-tab[data-sg-collections]')];
    const panels = [...body.querySelectorAll('[data-sg-collections]')]
      .filter((el) => !el.classList.contains('sg-tab'));
    if (!tabs.length && !panels.length) return;

    const assigned = (el) => (el.dataset.sgCollections || '').split(/\s+/).filter(Boolean);
    const isExplicit = (el) => {
      const on = assigned(el);
      return on.length > 0 && on.some((handle) => mine.includes(handle));
    };
    const isGeneric = (el) => assigned(el).length === 0;

    let keep;
    if (mine.length && [...tabs, ...panels].some(isExplicit)) {
      keep = isExplicit;
    } else if ([...tabs, ...panels].some(isGeneric)) {
      keep = isGeneric;
    } else {
      return;
    }

    tabs.forEach((tab) => { tab.hidden = !keep(tab); });
    panels.forEach((panel) => { panel.hidden = !keep(panel); });

    const keptTabs = tabs.filter(keep);
    const keptPanels = panels.filter(keep);

    // open the first surviving chart, and drop the tab strip when only one is left
    keptPanels.forEach((panel, i) => { panel.hidden = i !== 0; });
    if (keptTabs.length) {
      keptTabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        tab.tabIndex = i === 0 ? 0 : -1;
      });
      if (keptTabs.length === 1) keptTabs[0].closest('.sg-tabs')?.setAttribute('hidden', '');
    }
  };

  const initSizeGuide = (root) => {
    root.querySelectorAll('[data-size-guide-open]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        openSizeGuide(trigger);
      });
    });

    root.querySelectorAll('[data-size-guide-close]').forEach((button) => {
      button.addEventListener('click', () => button.closest('dialog')?.close());
    });

    // click the backdrop to dismiss
    root.querySelectorAll('dialog.mdc-size-guide').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });
  };

  /* -------------------------------------------------------------- lifecycle */

  const init = (root = document) => {
    root.querySelectorAll('.mdc-product__gallery').forEach(initGallery);
    initSizeGuide(root);
  };

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
})();
